import type {
  ContexteConversation,
  CorpsErreur,
  FicheClient,
  Prospects,
  Recherche,
  Relances,
  Sante,
  Todo,
} from './contrat';
import { activerDemo, demoDesactive, demoExclusif, resoudreDemo } from '../demo';
import { jeton } from './config';

const BASE_URL: string = (import.meta.env.VITE_API_URL ?? '/radar/api/v1').replace(/\/+$/, '');
const TTL_MS: number = Number(import.meta.env.VITE_CACHE_TTL ?? 60) * 1000;

/**
 * Erreur d'API portant le code du contrat.
 * `code` reste une chaîne libre : un code inconnu du plugin ne doit pas faire
 * tomber l'affichage, seulement retomber sur le message générique.
 */
export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly code: string | null,
    message: string,
    readonly indice: string | null = null,
    /** Secondes à attendre, renseigné sur un 429. */
    readonly retryApres: number | null = null,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }

  /** 409 : le pipeline n'a pas encore produit le bilan. Cas normal, pas une panne. */
  get bilanPasEncorePret(): boolean {
    return this.statut === 409 || this.code === 'donnees_indisponibles';
  }

  get conversationInconnue(): boolean {
    return this.statut === 404 || this.code === 'conversation_inconnue';
  }

  get jetonInvalide(): boolean {
    return this.statut === 401 || this.code === 'jeton_invalide';
  }
}

/**
 * Codes d'erreur définis par le contrat. Une réponse qui n'en porte aucun ne
 * vient pas de l'API attendue — endpoint pas encore écrit, page d'erreur du
 * reverse proxy, mauvaise URL. C'est ce qui déclenche le repli en démonstration.
 */
const CODES_DU_CONTRAT = new Set([
  'jeton_invalide',
  'conversation_inconnue',
  'donnees_indisponibles',
  'trop_de_requetes',
  'pipeline_indisponible',
  // Ajoutés le 30/08 : l'API les renvoie, le plugin les ignorait, et un code
  // ignoré fait basculer le panneau en données d'exemple — ce qui est pire
  // qu'un message d'erreur : l'utilisateur voit des chiffres qui ne sont pas
  // les siens. Un code présent dans la table du contrat n'est jamais un signe
  // que l'API n'est pas la bonne.
  'groupe_inconnu',
  'teammate_inconnu',
  'route_inconnue',
]);

/**
 * Cache mémoire par URL. Front réémet son contexte à chaque interaction de
 * l'utilisateur ; sans cache, la même conversation serait redemandée en boucle.
 * Les erreurs ne sont pas mises en cache, sauf le 409 : tant que le pipeline
 * n'a pas tourné, réessayer à chaque frappe ne sert à rien.
 */
interface Entree {
  expire: number;
  valeur?: unknown;
  erreur?: ErreurApi;
}
const cache = new Map<string, Entree>();

/**
 * Le panneau vit aussi longtemps que l'onglet Front : le cache est borné pour
 * qu'une journée de navigation ne l'engraisse pas indéfiniment. Map conserve
 * l'ordre d'insertion, la plus ancienne entrée part la première.
 */
const TAILLE_MAX = 100;

function ranger(url: string, entree: Entree): void {
  const maintenant = Date.now();
  for (const [cle, valeur] of cache) {
    if (valeur.expire <= maintenant) cache.delete(cle);
  }
  cache.delete(url);
  cache.set(url, entree);
  while (cache.size > TAILLE_MAX) {
    const plusAncienne = cache.keys().next().value;
    if (plusAncienne === undefined) break;
    cache.delete(plusAncienne);
  }
}

async function lireCorps(reponse: Response): Promise<CorpsErreur | null> {
  // Le contrat impose un corps JSON, mais un 502 du reverse proxy renvoie du
  // HTML : ne jamais laisser JSON.parse faire tomber l'interface.
  const texte = await reponse.text().catch(() => '');
  if (!texte.trim()) return null;
  try {
    return JSON.parse(texte) as CorpsErreur;
  } catch {
    return null;
  }
}

const MESSAGES: Record<number, string> = {
  401: "Jeton refusé par l'API.",
  404: "Cette conversation n'est pas dans le référentiel.",
  409: "Le bilan n'a pas encore été produit.",
  429: 'Trop de requêtes.',
  503: 'Pipeline indisponible.',
};

/**
 * Sert le chemin depuis les données d'exemple.
 * `bascule` indique qu'on y arrive par repli, et non par choix explicite :
 * l'interface doit alors afficher sa bannière de démonstration.
 */
function servirDemo<T>(chemin: string, bascule: boolean): T {
  const reponse = resoudreDemo(chemin);
  if (!reponse.trouve) {
    throw new ErreurApi(0, null, "Chemin non couvert par les données d'exemple.");
  }
  if (bascule) activerDemo();

  if ('statut' in reponse) {
    const corps = reponse.corps as CorpsErreur;
    throw new ErreurApi(
      reponse.statut,
      corps?.erreur ?? null,
      corps?.message ?? `Erreur ${reponse.statut}`,
      corps?.indice ?? null,
    );
  }
  return reponse.valeur as T;
}

async function appel<T>(chemin: string, signal?: AbortSignal, forcer = false): Promise<T> {
  // `VITE_MODE_DEMO=toujours` : le réseau n'est même pas tenté.
  if (demoExclusif) return servirDemo<T>(chemin, false);

  const url = `${BASE_URL}${chemin}`;
  const maintenant = Date.now();

  const enCache = cache.get(url);
  if (!forcer && enCache && enCache.expire > maintenant) {
    if (enCache.erreur) throw enCache.erreur;
    return enCache.valeur as T;
  }

  // Lu dans /config.json au premier appel, puis mémorisé. Repli sur la valeur
  // du build quand le fichier n'existe pas — c'est le cas en `npm run dev`.
  const porteur = await jeton();

  let reponse: Response;
  try {
    reponse = await fetch(url, {
      signal,
      headers: {
        // Jeton en en-tête uniquement, jamais dans l'URL.
        Authorization: `Bearer ${porteur}`,
        Accept: 'application/json',
      },
    });
  } catch (cause) {
    if ((cause as Error).name === 'AbortError') throw cause;
    // API injoignable : tant que le pipeline ne sert rien, la démonstration
    // vaut mieux qu'un panneau vide — à condition de le dire.
    if (!demoDesactive) return servirDemo<T>(chemin, true);
    throw new ErreurApi(
      0,
      null,
      `API injoignable (${(cause as Error).message}). Vérifiez VITE_API_URL et ALLOWED_ORIGINS côté serveur.`,
    );
  }

  const corps = await lireCorps(reponse);

  if (!reponse.ok) {
    // Une erreur hors contrat n'est pas une réponse de l'API attendue :
    // endpoint pas encore écrit, mauvaise URL, page du reverse proxy.
    const horsContrat = !corps?.erreur || !CODES_DU_CONTRAT.has(corps.erreur);
    if (horsContrat && !demoDesactive) return servirDemo<T>(chemin, true);

    const entete = reponse.headers.get('Retry-After');
    const erreur = new ErreurApi(
      reponse.status,
      corps?.erreur ?? null,
      corps?.message ?? MESSAGES[reponse.status] ?? `Erreur HTTP ${reponse.status}`,
      corps?.indice ?? null,
      entete ? Number(entete) : null,
    );
    if (erreur.bilanPasEncorePret) {
      ranger(url, { expire: maintenant + TTL_MS, erreur });
    }
    throw erreur;
  }

  ranger(url, { expire: maintenant + TTL_MS, valeur: corps });
  return corps as T;
}

/**
 * POST, pour la seule écriture de l'API.
 *
 * Volontairement à part de `appel` : celui-ci met en cache, se replie sur les
 * données d'exemple et avale les erreurs, trois comportements qu'on ne veut
 * surtout pas quand l'utilisateur croit avoir enregistré quelque chose. Ici une
 * erreur doit se voir.
 */
async function envoyer<T>(chemin: string, corps: unknown): Promise<T> {
  const porteur = await jeton();
  let reponse: Response;
  try {
    reponse = await fetch(`${BASE_URL}${chemin}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${porteur}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(corps),
    });
  } catch (cause) {
    throw new ErreurApi(0, null, `API injoignable (${(cause as Error).message}).`);
  }
  const lu = await lireCorps(reponse);
  if (!reponse.ok) {
    throw new ErreurApi(
      reponse.status,
      lu?.erreur ?? null,
      lu?.message ?? MESSAGES[reponse.status] ?? `Erreur HTTP ${reponse.status}`,
      lu?.indice ?? null,
    );
  }
  return lu as T;
}

export const api = {
  sante: (signal?: AbortSignal) => appel<Sante>('/sante', signal),

  contexte: (cnvId: string, signal?: AbortSignal, forcer?: boolean) =>
    appel<ContexteConversation>(
      `/conversation/${encodeURIComponent(cnvId)}/contexte`,
      signal,
      forcer,
    ),

  /**
   * `groupeId` vient toujours de `rattachement.groupe_id`. Ne jamais le
   * reconstruire depuis un nom de société : le rapprochement est la partie
   * difficile du pipeline, toute logique dupliquée ici divergerait.
   */
  client: (groupeId: string, signal?: AbortSignal, forcer?: boolean) =>
    appel<FicheClient>(`/client/${encodeURIComponent(groupeId)}`, signal, forcer),

  todo: (teammateId: string, signal?: AbortSignal, forcer?: boolean) =>
    appel<Todo>(`/todo/${encodeURIComponent(teammateId)}`, signal, forcer),

  /** Les sociétés hors référentiel Dolibarr. Sans identifiant : c'est une
   *  vue d'ensemble, pas le dossier d'un collaborateur. */
  prospects: (
    categorie?: string,
    exclus = false,
    signal?: AbortSignal,
    forcer?: boolean,
  ) => {
    const q = new URLSearchParams();
    if (categorie) q.set('categorie', categorie);
    if (exclus) q.set('exclus', 'true');
    q.set('limite', '2000');
    return appel<Prospects>(`/prospects?${q.toString()}`, signal, forcer);
  },

  /** Écarte définitivement une société de la liste. Réversible. */
  exclureProspect: (domaine: string, motif?: string, auteur?: string) =>
    envoyer<{ ok: boolean; message: string }>(
      `/prospects/${encodeURIComponent(domaine)}/exclure`,
      { motif, auteur },
    ),

  reintegrerProspect: (domaine: string) =>
    envoyer<{ ok: boolean; reintegree: boolean }>(
      `/prospects/${encodeURIComponent(domaine)}/reintegrer`,
      {},
    ),

  /**
   * La seule écriture du plugin : l'explication qu'un humain donne sur un
   * domaine. Elle ne modifie aucune donnée du pipeline — le tri automatique la
   * relira et se corrigera de lui-même.
   */
  noterProspect: (domaine: string, texte: string, auteur?: string) =>
    envoyer<{ ok: boolean; message: string }>(
      `/prospects/${encodeURIComponent(domaine)}/note`,
      { texte, auteur },
    ),

  /**
   * Confie une tâche au pipeline. Rien n'est exécuté au clic : la demande est
   * mise en file, et le traitement dépose un brouillon dans Front — jamais un
   * envoi. C'est l'équivalent au fil de l'eau des colonnes du classeur Excel.
   */
  demander: (corps: {
    type: 'relance' | 'reprise_contact' | 'analyse';
    conv_id?: string;
    tiers_id?: number;
    client?: string;
    instruction?: string;
    au_nom_de?: string;
    auteur?: string;
  }) => envoyer<{ ok: boolean; id: number; etat: string; message: string }>(
    '/demandes',
    corps,
  ),

  collaborateurs: (signal?: AbortSignal) =>
    appel<{ total: number; elements: Array<{ id: string; nom: string; email: string }> }>(
      '/collaborateurs',
      signal,
    ),

  /**
   * Enregistre une décision prise depuis la boîte d'actions : abandonner,
   * réaffecter, expliquer. Les relances passent par `demander`, qui a sa file.
   */
  agir: (corps: {
    type: 'abandon' | 'affectation' | 'explication';
    conv_id?: string;
    tiers_id?: number;
    projet_ref?: string;
    domaine?: string;
    client?: string;
    texte?: string;
    au_nom_de?: string;
    auteur?: string;
  }) => envoyer<{ ok: boolean; type: string; message: string }>('/actions', corps),

  relances: (teammateId: string, signal?: AbortSignal, forcer?: boolean) =>
    appel<Relances>(`/relances/${encodeURIComponent(teammateId)}`, signal, forcer),

  /**
   * Demande au pipeline de refaire ses listes maintenant, sans attendre la
   * nuit. Reprend Dolibarr et les décisions du plugin — pas ce qui s'est dit
   * dans Front depuis la dernière synchro, qui demanderait des heures.
   */
  recalculer: () =>
    envoyer<{ ok: boolean; demarre: boolean; etat: string; message: string }>(
      '/rafraichir',
      {},
    ),

  /** Où en est le recalcul. Interrogé en boucle pendant qu'il tourne. */
  etatRecalcul: (signal?: AbortSignal) =>
    appel<{ en_cours: boolean; etat: string; bilan_genere_le: string | null }>(
      '/rafraichir',
      signal,
      true, // jamais depuis le cache : c'est justement ce qui change
    ),

  /**
   * Recherche plein texte française, reclassée par le modèle côté pipeline.
   * Le débounce est à la charge de l'appelant : le contrat impose 400 ms au
   * minimum, `Recherche.tsx` s'en occupe.
   */
  recherche: (question: string, limite = 20, signal?: AbortSignal) =>
    appel<Recherche>(
      `/recherche?q=${encodeURIComponent(question)}&limite=${limite}`,
      signal,
    ),
};
